import React from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { CARD_HELP_TEXT, CardHeadingWithHelp } from "../shared/help";

const BLANK_COMPONENT = {
  key: "",
  type: "EARNING",
  calculationType: "FIXED",
  value: 0,
  formula: "",
  isTaxable: true,
  isPFApplicable: false,
  isESIApplicable: false,
};

const SalaryComponentsEditor = ({ components, onChange }) => {
  const updateRow = (idx, field, value) => {
    const updated = components.map((c, i) =>
      i === idx ? { ...c, [field]: value } : c,
    );
    onChange(updated);
  };

  const addRow = () => onChange([...components, { ...BLANK_COMPONENT }]);
  const removeRow = (idx) => onChange(components.filter((_, i) => i !== idx));

  return (
    <Stack spacing={3}>
      {components.map((comp, idx) => (
        <Box
          key={idx}
          p={3}
          borderWidth={1}
          borderRadius="md"
          bg="gray.50"
          _dark={{ bg: "gray.700" }}
        >
          <HStack spacing={2} flexWrap="wrap" alignItems="flex-start">
            <FormControl minW="110px" flex="1">
              <FormLabel fontSize="xs" mb={1}>
                Key
              </FormLabel>
              <Input
                size="sm"
                placeholder="e.g. BASIC"
                value={comp.key}
                onChange={(e) =>
                  updateRow(idx, "key", e.target.value.toUpperCase())
                }
              />
            </FormControl>

            <FormControl minW="110px" flex="1">
              <FormLabel fontSize="xs" mb={1}>
                Type
              </FormLabel>
              <Select
                size="sm"
                value={comp.type}
                onChange={(e) => updateRow(idx, "type", e.target.value)}
              >
                <option value="EARNING">Earning</option>
                <option value="DEDUCTION">Deduction</option>
              </Select>
            </FormControl>

            <FormControl minW="150px" flex="1">
              <FormLabel fontSize="xs" mb={1}>
                Calculation
              </FormLabel>
              <Select
                size="sm"
                value={comp.calculationType}
                onChange={(e) =>
                  updateRow(idx, "calculationType", e.target.value)
                }
              >
                <option value="FIXED">Fixed</option>
                <option value="PERCENT_OF_BASIC">% of Basic</option>
                <option value="PERCENT_OF_CTC">% of CTC</option>
                <option value="FORMULA">Formula</option>
              </Select>
            </FormControl>

            {comp.calculationType !== "FORMULA" && (
              <FormControl minW="90px" flex="1">
                <FormLabel fontSize="xs" mb={1}>
                  Value
                </FormLabel>
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  value={comp.value}
                  onChange={(e) =>
                    updateRow(idx, "value", Number(e.target.value))
                  }
                />
              </FormControl>
            )}

            {comp.calculationType === "FORMULA" && (
              <FormControl minW="150px" flex="2">
                <FormLabel fontSize="xs" mb={1}>
                  Formula
                </FormLabel>
                <Input
                  size="sm"
                  placeholder="e.g. BASIC * 0.12"
                  value={comp.formula || ""}
                  onChange={(e) => updateRow(idx, "formula", e.target.value)}
                />
              </FormControl>
            )}

            <VStack spacing={1} alignSelf="flex-end" pb={1} minW="85px">
              <Checkbox
                size="sm"
                isChecked={comp.isTaxable}
                onChange={(e) => updateRow(idx, "isTaxable", e.target.checked)}
              >
                Taxable
              </Checkbox>
              <Checkbox
                size="sm"
                isChecked={comp.isPFApplicable}
                onChange={(e) =>
                  updateRow(idx, "isPFApplicable", e.target.checked)
                }
              >
                PF
              </Checkbox>
              <Checkbox
                size="sm"
                isChecked={comp.isESIApplicable}
                onChange={(e) =>
                  updateRow(idx, "isESIApplicable", e.target.checked)
                }
              >
                ESI
              </Checkbox>
            </VStack>

            <IconButton
              aria-label="Remove component"
              icon={<DeleteIcon />}
              size="sm"
              colorScheme="red"
              variant="ghost"
              onClick={() => removeRow(idx)}
              alignSelf="flex-end"
            />
          </HStack>
        </Box>
      ))}

      <Button
        size="sm"
        variant="outline"
        onClick={addRow}
        alignSelf="flex-start"
      >
        + Add Component
      </Button>
    </Stack>
  );
};

const CtcSection = ({
  canManagePayroll,
  structureForm,
  setStructureForm,
  handleCreateStructure,
  compensationForm,
  setCompensationForm,
  employees,
  salaryStructures,
  handleAssignCompensation,
  compensations,
  meta,
  formatPayrollCurrency,
}) => {
  return (
    <Stack spacing={6}>
      {canManagePayroll ? (
        <>
          <Card>
            <CardHeader>
              <CardHeadingWithHelp
                title="Create Salary Structure"
                helpText={CARD_HELP_TEXT.createStructure}
              />
            </CardHeader>
            <CardBody>
              <Stack spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Name</FormLabel>
                    <Input
                      value={structureForm.name}
                      onChange={(e) =>
                        setStructureForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Code</FormLabel>
                    <Input
                      value={structureForm.code}
                      onChange={(e) =>
                        setStructureForm((prev) => ({
                          ...prev,
                          code: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>CTC (Rupees)</FormLabel>
                    <Input
                      type="number"
                      value={structureForm.ctc}
                      onChange={(e) =>
                        setStructureForm((prev) => ({
                          ...prev,
                          ctc: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Effective From</FormLabel>
                    <Input
                      type="date"
                      value={structureForm.effectiveFrom}
                      onChange={(e) =>
                        setStructureForm((prev) => ({
                          ...prev,
                          effectiveFrom: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                </SimpleGrid>
                <FormControl>
                  <FormLabel>Salary Components</FormLabel>
                  <SalaryComponentsEditor
                    components={structureForm.components}
                    onChange={(updated) =>
                      setStructureForm((prev) => ({
                        ...prev,
                        components: updated,
                      }))
                    }
                  />
                </FormControl>
                <Button
                  colorScheme="blue"
                  onClick={handleCreateStructure}
                  alignSelf="flex-start"
                >
                  Save Structure
                </Button>
              </Stack>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardHeadingWithHelp
                title="Assign Employee Compensation"
                helpText={CARD_HELP_TEXT.assignCompensation}
              />
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Employee</FormLabel>
                  <Select
                    value={compensationForm.userId}
                    onChange={(e) =>
                      setCompensationForm((prev) => ({
                        ...prev,
                        userId: e.target.value,
                      }))
                    }
                  >
                    <option value="">Select employee</option>
                    {employees.map((employee) => (
                      <option key={employee._id} value={employee._id}>
                        {employee.name} ({employee.employeeId || employee.email}
                        )
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Salary Structure</FormLabel>
                  <Select
                    value={compensationForm.salaryStructureId}
                    onChange={(e) =>
                      setCompensationForm((prev) => ({
                        ...prev,
                        salaryStructureId: e.target.value,
                      }))
                    }
                  >
                    <option value="">Select structure</option>
                    {salaryStructures.map((structure) => (
                      <option key={structure._id} value={structure._id}>
                        {structure.name} ({structure.code})
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Base Salary (Rupees)</FormLabel>
                  <Input
                    type="number"
                    value={compensationForm.baseSalary}
                    onChange={(e) =>
                      setCompensationForm((prev) => ({
                        ...prev,
                        baseSalary: e.target.value,
                      }))
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Effective From</FormLabel>
                  <Input
                    type="date"
                    value={compensationForm.effectiveFrom}
                    onChange={(e) =>
                      setCompensationForm((prev) => ({
                        ...prev,
                        effectiveFrom: e.target.value,
                      }))
                    }
                  />
                </FormControl>
                <FormControl gridColumn={{ md: "1 / span 2" }}>
                  <FormLabel>Variables JSON</FormLabel>
                  <Textarea
                    rows={4}
                    value={compensationForm.variablesJson}
                    onChange={(e) =>
                      setCompensationForm((prev) => ({
                        ...prev,
                        variablesJson: e.target.value,
                      }))
                    }
                  />
                </FormControl>
              </SimpleGrid>
              <Button
                mt={4}
                colorScheme="blue"
                onClick={handleAssignCompensation}
              >
                Assign Compensation
              </Button>
            </CardBody>
          </Card>
        </>
      ) : null}

      <Card>
        <CardHeader>
          <CardHeadingWithHelp
            title="Salary Structures"
            helpText={CARD_HELP_TEXT.salaryStructures}
          />
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Code</Th>
                  <Th>CTC</Th>
                  <Th>Components</Th>
                  <Th>Effective</Th>
                </Tr>
              </Thead>
              <Tbody>
                {salaryStructures.map((structure) => (
                  <Tr key={structure._id}>
                    <Td>{structure.name}</Td>
                    <Td>{structure.code}</Td>
                    <Td>{formatPayrollCurrency(structure.ctc, meta)}</Td>
                    <Td>{structure.components?.length || 0}</Td>
                    <Td>
                      {new Date(structure.effectiveFrom).toLocaleDateString()}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardHeadingWithHelp
            title="Compensation Assignments"
            helpText={CARD_HELP_TEXT.compensationAssignments}
          />
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Employee</Th>
                  <Th>Structure</Th>
                  <Th>Base Salary</Th>
                  <Th>Effective From</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {compensations.map((record) => (
                  <Tr key={record._id}>
                    <Td>{record.userId?.name || "-"}</Td>
                    <Td>{record.salaryStructureId?.name || "-"}</Td>
                    <Td>{formatPayrollCurrency(record.baseSalary, meta)}</Td>
                    <Td>
                      {new Date(record.effectiveFrom).toLocaleDateString()}
                    </Td>
                    <Td>
                      <Badge colorScheme={record.isActive ? "green" : "gray"}>
                        {record.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>
    </Stack>
  );
};

export default CtcSection;
